import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { AuthContext } from "../App";
import { getUserRole } from "./utilities/getUserRole";
import { FiUser, FiSettings, FiLogOut, FiChevronDown, FiLock, FiRefreshCw } from "react-icons/fi";
import { MdAdminPanelSettings } from "react-icons/md";
import { IoMdLock } from "react-icons/io";
import { BsUnlockFill } from "react-icons/bs";
import axios from "axios";
import { useToast } from '@chakra-ui/react';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user: authUser } = useContext(AuthContext) || {};
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const toast = useToast();

  // Password update state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Get user info from localStorage
  const user = getUserRole();
  const userRole = user?.role || "admin";
  const userName = user?.fullName || user?.email || "User";
  const userEmail = user?.email || "";
  const adminId = user?.id || user?._id;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    if (logout) logout();
    navigate("/login");
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      if (passwordData.newPassword !== confirmPassword) {
        toast({
          title: "New Password and Confirm Password do not match!",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      await axios.put(
        `/api/v1/adminManagment/admins-change-password`,
        passwordData
      );
      toast({
        title: "Password updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setShowPasswordModal(false);
      setPasswordData({ currentPassword: "", newPassword: "" });
      setConfirmPassword("");
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.message || "Error updating password";
      toast({
        title: message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const isSuperAdmin = userRole === "super-admin";

  return (
    <header className="fixed top-0 left-64 right-0 h-16 bg-white border-b-2 border-gray-200 z-40 flex items-center justify-between px-6 shadow-sm backdrop-blur-sm bg-white/95">
      {/* Left side - can add breadcrumbs or page title here */}
      <div className="flex-1">
        {/* Optional: Add page title or breadcrumbs here */}
      </div>

      {/* Right side - Auto refresh and User dropdown */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => window.location.reload()}
          className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all
                     bg-gradient-to-br from-white via-gray-50 to-gray-100
                     border-2 border-gray-300 border-t-gray-200 border-l-gray-200
                     shadow-[0_4px_8px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06),inset_0_1px_0_rgba(255,255,255,0.8)]
                     hover:shadow-[0_6px_12px_rgba(0,0,0,0.15),0_4px_8px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.9)]
                     hover:translate-y-[-1px] active:translate-y-[0px]
                     active:shadow-[0_2px_4px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06),inset_0_2px_4px_rgba(0,0,0,0.1)]
                     text-[var(--primary-solid)] hover:text-[var(--primary-dark)]"
          title="Refresh Page"
        >
          {/* Shine effect overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none"></div>

          <FiRefreshCw className="text-lg relative z-10" />
          <span className="font-medium relative z-10 hidden sm:inline">Refresh</span>
        </button>

        <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setUserDropdownOpen(!userDropdownOpen)}
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all relative overflow-hidden
                     bg-gradient-to-br from-white via-gray-50 to-gray-100
                     border-2 border-gray-300 border-t-gray-200 border-l-gray-200
                     shadow-[0_4px_8px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06),inset_0_1px_0_rgba(255,255,255,0.8)]
                     hover:shadow-[0_6px_12px_rgba(0,0,0,0.15),0_4px_8px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.9)]
                     hover:translate-y-[-1px] active:translate-y-[0px]
                     active:shadow-[0_2px_4px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06),inset_0_2px_4px_rgba(0,0,0,0.1)]"
        >
          {/* Shine effect overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none"></div>
          
          <div className="w-10 h-10 bg-gradient-to-br from-[var(--primary-solid)] to-[var(--primary-dark)] rounded-full flex items-center justify-center shadow-[0_3px_6px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.3)] relative z-10">
            <FiUser className="text-white text-lg drop-shadow-sm" />
          </div>
          <div className="text-left hidden md:block relative z-10">
            <p className="text-sm font-semibold text-[var(--primary-solid)] drop-shadow-sm">{userName}</p>
            <p className="text-xs text-[var(--primary-solid)]/70 capitalize">{userRole}</p>
          </div>
          <FiChevronDown
            className={`text-[var(--primary-solid)] transition-transform relative z-10 ${
              userDropdownOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Dropdown Menu */}
        {userDropdownOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-white border-2 border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden backdrop-blur-sm">
            {/* User Info Section */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[var(--primary-solid)] to-[var(--primary-dark)] rounded-full flex items-center justify-center shadow-md">
                  <FiUser className="text-white text-xl" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-[var(--primary-solid)]">{userName}</p>
                  <p className="text-xs text-[var(--primary-solid)]/70">{userEmail}</p>
                  <p className="text-xs text-[var(--primary-solid)]/70 capitalize mt-1">
                    Role: {userRole}
                  </p>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              {isSuperAdmin && (
                <>
                  <Link
                    to="/admin-managment"
                    onClick={() => setUserDropdownOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 transition-all ${
                      location.pathname === "/admin-managment"
                        ? "bg-[var(--primary-solid)] text-white shadow-md"
                        : "text-[var(--primary-solid)] hover:bg-[var(--primary-solid)]/10"
                    }`}
                  >
                    <MdAdminPanelSettings className="text-lg" />
                    <span>Employee Management</span>
                  </Link>

                  <Link
                    to="/settings"
                    onClick={() => setUserDropdownOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 transition-all ${
                      location.pathname === "/settings"
                        ? "bg-[var(--primary-solid)] text-white shadow-md"
                        : "text-[var(--primary-solid)] hover:bg-[var(--primary-solid)]/10"
                    }`}
                  >
                    <FiSettings className="text-lg" />
                    <span>Settings</span>
                  </Link>
                </>
              )}

              <button
                onClick={() => {
                  setShowPasswordModal(true);
                  setUserDropdownOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-[var(--primary-solid)] hover:bg-[var(--primary-solid)]/10 transition-all text-left"
              >
                <FiLock className="text-lg" />
                <span>Password Update</span>
              </button>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 transition-all text-left"
              >
                <FiLogOut className="text-lg" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Password Update Modal */}
      {showPasswordModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[100]" onClick={() => {
            setShowPasswordModal(false);
            setPasswordData({ currentPassword: "", newPassword: "" });
            setConfirmPassword("");
          }}></div>
          <div className="fixed inset-0 flex justify-center items-center z-[100] pointer-events-none">
            <div className="bg-white border-2 border-gray-200 rounded-xl w-full max-w-md p-6 shadow-xl mx-4 pointer-events-auto">
            <h3 className="text-2xl font-bold mb-6 text-[var(--primary-solid)]">
              Change Password
            </h3>

            <form onSubmit={handlePasswordSubmit} className="space-y-5 mt-80">
              {/* CURRENT PASSWORD */}
              <div>
                <label className="block text-sm font-medium mb-1 text-[var(--primary-solid)]">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrent ? "text" : "password"}
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, currentPassword: e.target.value })
                    }
                    required
                    className="w-full bg-white text-[var(--black-text)] border-2 border-gray-200 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)] outline-none"
                  />
                  <span
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-3 top-2.5 cursor-pointer text-[var(--primary-solid)]"
                  >
                    {showCurrent ? <BsUnlockFill className="text-xl" /> : <IoMdLock className="text-xl" />}
                  </span>
                </div>
              </div>

              {/* NEW PASSWORD */}
              <div >
                <label className="block text-sm font-medium mb-1 text-[var(--primary-solid)]">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNew ? "text" : "password"}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, newPassword: e.target.value })
                    }
                    required
                    className="w-full bg-white text-[var(--black-text)] border-2 border-gray-200 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)] outline-none"
                  />
                  <span
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-2.5 cursor-pointer text-[var(--primary-solid)]"
                  >
                    {showNew ? <BsUnlockFill className="text-xl" /> : <IoMdLock className="text-xl" />}
                  </span>
                </div>
              </div>

              {/* CONFIRM PASSWORD */}
              <div>
                <label className="block text-sm font-medium mb-1 text-[var(--primary-solid)]">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full bg-white text-[var(--black-text)] border-2 border-gray-200 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)] outline-none"
                  />
                  <span
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-2.5 cursor-pointer text-[var(--primary-solid)]"
                  >
                    {showConfirm ? <BsUnlockFill className="text-xl" /> : <IoMdLock className="text-xl" />}
                  </span>
                </div>
              </div>

              {/* BUTTONS */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({ currentPassword: "", newPassword: "" });
                    setConfirmPassword("");
                  }}
                  className="px-4 py-2 rounded-lg border-2 border-gray-200 text-[var(--primary-solid)] bg-white hover:bg-gray-50 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-[var(--primary-solid)] to-[var(--primary-dark)] text-white font-semibold hover:shadow-md transition-all"
                >
                  Update Password
                </button>
              </div>
            </form>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;

